import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router";
import { useFetchAuthorisation, useFetchUserData } from "../../utils/AppFunctions";
import "react-toastify/dist/ReactToastify.css";
import AdminLayout from "../../layouts/AdminLayout";
import { create_new_contact_group, delete_contact_group, fetch_contact_group_by_id, update_contact_group_by_id } from "../../api/campaignsAPI";
import notify from "../../utils/IANotification";
import { MdArrowBack, MdSave, MdDelete } from "react-icons/md";
import Select from 'react-select'
import { fetch_all_users_admin } from "../../api/usersAPI";
import SubNavbar from "../../components/ui/SubNavbar";
import Card from "../../components/ui/Card";
import Button from "../../components/ui/Button";

export default function CampaignGroupFormPage() {
  const navigate = useNavigate();
  useFetchAuthorisation("user");
  useFetchUserData();

  const searchParams = new URLSearchParams(window.location.search);
  const group_id = searchParams.get("group_id") || searchParams.get("id");

  const [activeTab, setActiveTab] = useState("general");
  const [groupData, setGroupData] = useState({
    name: "",
    description: "",
    user_ids: [],
    email_list: "",
  })
  const [loading, setLoading] = useState(group_id && group_id !== "new")
  const [users, setUsers] = useState([])

  const tabs = [
    { id: 'general', label: 'Group Info' },
    { id: 'contacts', label: 'Recipients & Email List' },
  ]

  useEffect(() => {
    fetchPlatformUsers()
    if (group_id && group_id !== "new") {
      fetchCampaignGroupById()
    } else {
      setLoading(false)
    }
  }, [group_id])

  const fetchPlatformUsers = async () => {
    try {
      const data = await fetch_all_users_admin()
      if (data?.status === 200 && Array.isArray(data.data)) {
        setUsers(data.data)
      }
    } catch (err) {
      console.error("Failed to load platform users:", err)
    }
  }

  const fetchCampaignGroupById = async () => {
    if (!group_id || group_id === "new") {
      setLoading(false)
      return
    }
    setLoading(true)
    try {
      const { data, error, message } = await fetch_contact_group_by_id(group_id)
      if (error) notify({ message: message || error, type: 'error' })
      if (data && typeof data === 'object' && Object.keys(data).length > 0) {
        let userIds = []
        if (Array.isArray(data.user_ids)) {
          userIds = data.user_ids
        } else if (typeof data.user_ids === 'string' && data.user_ids.trim()) {
          try {
            const parsed = JSON.parse(data.user_ids)
            if (Array.isArray(parsed)) userIds = parsed
            else userIds = data.user_ids.split(',').map(s => s.trim()).filter(Boolean)
          } catch (e) {
            userIds = data.user_ids.split(',').map(s => s.trim()).filter(Boolean)
          }
        }

        let emailList = ""
        if (Array.isArray(data.email_list)) {
          emailList = data.email_list.join("\n")
        } else if (typeof data.email_list === 'string') {
          emailList = data.email_list
        }

        setGroupData({
          ...data,
          name: data.name || "",
          description: data.description || "",
          user_ids: userIds,
          email_list: emailList
        })
      }
    } catch (err) {
      console.error("Failed to load group by id:", err)
      notify({ message: "Failed to load group data", type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const getPayload = () => {
    let emails = []
    if (typeof groupData.email_list === 'string') {
      emails = groupData.email_list.split(/[\n,]+/).map(s => s.trim()).filter(Boolean)
    } else if (Array.isArray(groupData.email_list)) {
      emails = groupData.email_list
    }

    return {
      ...groupData,
      name: groupData.name?.trim(),
      description: groupData.description?.trim(),
      user_ids: Array.isArray(groupData.user_ids) ? groupData.user_ids : [],
      email_list: emails
    }
  }

  const createNewCampaignGroup = async () => {
    if (!groupData.name?.trim()) {
      return notify({ message: "Group name is required", type: 'warning' })
    }
    const payload = getPayload()
    const { data, error, message } = await create_new_contact_group(payload)
    if (error) return notify({ message: message || error, type: 'error' })
    notify({ message: "Campaign group created successfully", type: 'success' })
    const createdId = data?.id || data?.data?.id
    navigate(`/admin/campaigns/groups-tags/details?group_id=${createdId}`)
  }

  const updateContactGroupById = async () => {
    if (!group_id || group_id === "new") return
    if (!groupData.name?.trim()) {
      return notify({ message: "Group name is required", type: 'warning' })
    }
    const payload = getPayload()
    const { error, message } = await update_contact_group_by_id(group_id, payload)
    if (error) return notify({ message: message || error, type: 'error' })
    notify({ message: "Campaign group updated successfully", type: 'success' })
    fetchCampaignGroupById()
  }

  const deleteContactGroupById = async () => {
    if (!group_id || group_id === "new") return
    const { error, message } = await delete_contact_group(group_id)
    if (error) return notify({ message: message || error, type: 'error' })
    notify({ message: "Campaign group deleted successfully", type: 'success' })
    navigate("/admin/campaigns/groups-tags")
  }

  return (
    <AdminLayout
      navSelected={"Groups & Tags"}
      headerText={group_id === "new" ? "New Campaign Contact Group" : "Edit Campaign Group"}
      toolbar={
        <>
          <div className="flex items-center gap-3">
            <Button variant="outline" icon={MdArrowBack} onClick={() => navigate('/admin/campaigns/groups-tags')}>
              Back
            </Button>
            <h1 className="text-xl font-bold text-gray-900">
              {group_id === "new" ? "Create Group" : groupData.name || "Edit Group"}
            </h1>
          </div>
          <div className="flex gap-2">
            {group_id !== "new" && (
              <Button variant="danger" icon={MdDelete} onClick={deleteContactGroupById}>
                Delete
              </Button>
            )}
            <Button variant="primary" icon={MdSave} onClick={() => {
              if (group_id === "new") createNewCampaignGroup()
              else updateContactGroupById()
            }}>
              {group_id === "new" ? "Create" : "Save Changes"}
            </Button>
          </div>
        </>
      }
    >
      <SubNavbar
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />

      {loading ? (
        <div className="flex justify-center items-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-sm text-gray-600">Loading group details...</span>
        </div>
      ) : (
        <>
          {/* TAB 1: GROUP INFO */}
          {activeTab === "general" && (
            <Card title="Campaign Contact Group Information" subtitle="Group name and purpose description.">
              <div className="space-y-6">
                <div className="input-container">
                  <label className="input-label font-semibold">Group Name</label>
                  <input
                    type="text"
                    className="text-input"
                    value={groupData.name || ""}
                    onChange={(e) => setGroupData({ ...groupData, name: e.target.value })}
                    placeholder="e.g. Library Ministry Volunteers"
                  />
                </div>

                <div className="input-container">
                  <label className="input-label font-semibold">Description</label>
                  <textarea
                    className="textarea-input h-32"
                    value={groupData.description || ""}
                    onChange={(e) => setGroupData({ ...groupData, description: e.target.value })}
                    placeholder="e.g. Target audience group for library event announcements and newsletters..."
                  />
                </div>
              </div>
            </Card>
          )}

          {/* TAB 2: RECIPIENTS & EMAIL LIST */}
          {activeTab === "contacts" && (
            <Card title="Recipients & External Emails" subtitle="Assign platform users or paste additional email addresses.">
              <div className="space-y-6">
                <div className="input-container">
                  <label className="input-label font-semibold">Assigned Platform Users</label>
                  <Select
                    value={users.filter(user => (groupData.user_ids || []).includes(user.id)).map(user => ({
                      value: user.id,
                      label: `${user.first_name || ''} ${user.last_name || ''} (${user.email || ''})`.trim()
                    }))}
                    options={users.map(user => ({
                      value: user.id,
                      label: `${user.first_name || ''} ${user.last_name || ''} (${user.email || ''})`.trim()
                    }))}
                    isMulti
                    onChange={(selected) => setGroupData({ ...groupData, user_ids: (selected || []).map(s => s.value) })}
                    placeholder="Select registered users..."
                  />
                </div>

                <div className="input-container">
                  <label className="input-label font-semibold">External Email Addresses</label>
                  <textarea
                    className="textarea-input h-36 font-mono text-xs"
                    value={groupData.email_list || ""}
                    onChange={(e) => setGroupData({ ...groupData, email_list: e.target.value })}
                    placeholder="Enter external emails separated by comma or newlines (e.g. john@example.com)..."
                  />
                </div>
              </div>
            </Card>
          )}
        </>
      )}
    </AdminLayout>
  )
}